import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useEffect, useId } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/lib/utils';

/**
 * The chrome every session dialog shares, ported from the designer's
 * `.sc-session-modal__*` block (General/react-new-html → components/sessions/
 * UpdateSessionStatusModal.css, which is where those classes are defined and which the
 * Approve / Decline / Resubmission dialogs all reuse).
 *
 * It exists for the same reason the designer put those classes in one file: four dialogs
 * share one overlay, one container, one header, one footer and one pair of buttons. Copying
 * that into each would guarantee they drift apart on the first change.
 *
 * Exact values, not approximations: 16px radius, #ECEEF3 hairlines, the 34px header ring,
 * the 28px close button, the #FAFAFA footer, and the fade-up entrance.
 */
export const MODAL_GUTTER = 'px-[clamp(16px,1.5vw,22px)]';

/** The designer's two footer buttons — same geometry, different intent colour. */
export function ModalSecondaryButton({
  children,
  onClick,
  disabled,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex h-[clamp(36px,2.5vw,40px)] cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-[#D0D4E4] bg-white px-[clamp(16px,1.5vw,20px)] text-[clamp(13px,0.95vw,14px)] font-medium text-[#041A3A] transition-colors hover:border-gray-400 hover:bg-gray-50 disabled:opacity-50"
    >
      {children}
    </button>
  );
}

export function ModalPrimaryButton({
  children,
  onClick,
  disabled,
  title,
  /** `danger` is the designer's `.sc-session-modal__btn-danger`, used by Decline. */
  tone = 'primary',
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
  tone?: 'primary' | 'danger';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(
        'inline-flex h-[clamp(36px,2.5vw,40px)] cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg border-none px-[clamp(18px,1.6vw,22px)] text-[clamp(13px,0.95vw,14px)] font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        tone === 'danger' ? 'bg-[#BF3636] hover:bg-[#A32E2E]' : 'bg-[#0048FF] hover:bg-[#003CE0]',
      )}
    >
      {children}
    </button>
  );
}

/**
 * The designer's custom 18px checkbox — square, 2px border, filled blue when checked.
 * Shared by the Approve/Decline email toggle and the Resubmission check rows, which is
 * how the designer has it (`--checkbox` in both CSS files, identical values).
 */
export function ModalCheckbox({ checked }: { checked: boolean }) {
  return (
    <span
      className={cn(
        'flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded border-2 transition-colors duration-150',
        checked ? 'border-[#0048FF] bg-[#0048FF]' : 'border-[#D0D4E4] bg-white',
      )}
    >
      {checked && (
        <svg width="11" height="9" viewBox="0 0 11 9" fill="none" aria-hidden="true">
          <path
            d="M1 4.5L3.5 7L9.5 1"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </span>
  );
}

/** The designer's 18px round radio, taking the calling dialog's accent colour. */
export function ModalRadio({ checked, color }: { checked: boolean; color: string }) {
  return (
    <span
      className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border-2 bg-white transition-colors duration-150"
      style={{ borderColor: checked ? color : '#D0D4E4' }}
    >
      {checked && (
        <span className="h-[9px] w-[9px] rounded-full" style={{ backgroundColor: color }} />
      )}
    </span>
  );
}

export function SessionModalShell({
  open,
  onClose,
  icon,
  title,
  subtitle,
  maxWidth,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  /** The header's status mark, inside the designer's 34px ring. */
  icon: ReactNode;
  title: string;
  subtitle: ReactNode;
  /**
   * Wider than the designer's 500/540px. Their reason labels ("Liveness does not match
   * document photo") did not fit two to a row at 500px and were being clipped by the
   * container, and the check rows were forcing a horizontal scrollbar. Widening is the
   * honest fix rather than shrinking the type the designer chose.
   */
  maxWidth: string;
  children: ReactNode;
  footer: ReactNode;
}) {
  const titleId = useId();

  // Escape closes. Registered only while open so it cannot swallow the key elsewhere.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  // PORTALLED TO THE BODY, and that is not cosmetic. These dialogs are opened from the
  // verdict cell, which lives inside a table row whose onClick navigates to the session.
  // Rendered in place, every click inside the dialog — including Cancel and the close X —
  // bubbled up through the row and opened the session view behind it. A portal moves the
  // dialog out of that subtree, so no click inside it can reach the row at all. Same
  // reason the verdict dropdown is portalled.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-[clamp(12px,2vw,24px)]">
      {/* The click-outside target is a real BUTTON behind the dialog rather than a click
          handler on the overlay div: same behaviour, but focusable and keyboard-operable by
          construction, so nothing inside needs stopPropagation. */}
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 h-full w-full cursor-default bg-black/40 backdrop-blur-sm"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={{ maxWidth }}
        className="relative z-10 flex max-h-[90vh] w-full flex-col overflow-hidden rounded-2xl border border-[#ECEEF3] bg-white shadow-2xl"
      >
        {/* ── Header ── */}
        <div
          className={cn(
            'flex shrink-0 items-start justify-between gap-4 border-b border-[#ECEEF3] py-[clamp(14px,1.2vw,18px)]',
            MODAL_GUTTER,
          )}
        >
          {/* min-w-0 on the header and its text column: without it the subtitle's
              min-content width (a long applicant name plus the reference tag) sets the
              dialog's minimum width and pushes the close button off the edge. */}
          <div className="flex min-w-0 items-start gap-3">
            <div className="mt-0.5 flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full border-[1.5px] border-[#D0D4E4] bg-[#F8FAFE]">
              {icon}
            </div>
            <div className="flex min-w-0 flex-col gap-0.5">
              <h2
                id={titleId}
                className="m-0 text-[clamp(16px,1.3vw,19px)] font-semibold leading-tight text-[#041A3A]"
              >
                {title}
              </h2>
              <p className="m-0 flex flex-wrap items-center gap-1 text-[clamp(11.5px,0.9vw,13px)] font-normal leading-normal text-[#666666]">
                {subtitle}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-full border-none bg-transparent text-[#666666] transition-colors hover:bg-gray-100 hover:text-[#041A3A]"
          >
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>

        {/* ── Body: one scroll container, so a short viewport scrolls the content and
             leaves the header and footer where they are.

             `overflow-x-hidden` as well as `overflow-y-auto`, and `min-w-0`: a container
             that scrolls on one axis becomes a scroll container on BOTH, so anything a
             pixel too wide inside it produced a horizontal scrollbar and let the content be
             dragged sideways out from under the header — which is exactly what happened to
             the check list. The content is also made to fit on its own (see the min-w-0 and
             wrapping in each dialog); this is the guarantee, not the fix. ── */}
        <div className="flex min-w-0 flex-1 flex-col overflow-y-auto overflow-x-hidden">
          {children}
        </div>

        {/* ── Footer ── */}
        <div
          className={cn(
            'flex shrink-0 items-center justify-end gap-3 border-t border-[#ECEEF3] bg-[#FAFAFA] py-[clamp(12px,1vw,14px)]',
            MODAL_GUTTER,
          )}
        >
          {footer}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * The designer's header subtitle: a mono reference tag, then the facts about the run,
 * dot-separated, ending in the status in its own colour.
 *
 * Country is not among them — the designer's mock hard-codes "India" and we do not store a
 * country, so inventing one would put a fabricated fact in an audit dialog.
 */
export function ModalSubtitle({
  referenceId,
  applicantName,
  status,
  statusColor,
}: {
  referenceId?: string;
  applicantName: string;
  status: string;
  statusColor: string;
}) {
  return (
    <>
      {referenceId && (
        <span className="inline-block rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[11px] font-medium leading-none text-[#041A3A]">
          {referenceId}
        </span>
      )}
      <span>
        {referenceId ? '· ' : ''}
        {applicantName} ·{' '}
      </span>
      <span className="font-semibold" style={{ color: statusColor }}>
        {status}
      </span>
    </>
  );
}
